
/**
 * Event configurations:
 * - carContext - If true car context object is injected to analytic event, look more for getCarContextData() function and ga4.js.
 */
export const EVENTS = {
  car_purchased: { carContext: true },
  car_request_funding: { carContext: true },
  car_reserved: { carContext: true },
  car_maintenance_reserved: {carContext: true },

  form_submit: { carContext: false },
  click_contact: { carContext: true },
  click_cta: { carContext: false },
  car_search_done: { carContext: false },
  car_text_search: { carContext: false },
  car_card_click: { carContext: true },
  car_page_click: { carContext: true },
  navigation_click: { carContext: false },
  tab_click: { carContext: true },

  funding_success: { carContext: true },
  funding_calculator_done: { carContext: true },

  page_view: { carContext: true },
  car_click_from_search: { carContext: true },

}

/**
 * List of HTML elements considered to be interactive
 *
 * There is this HTML -specs https://html.spec.whatwg.org/multipage/interactive-elements.html#interactive-elements
 * but if we have some custom elements we need to think definition more..
 */
const interactiveElements = [
  HTMLLinkElement, HTMLButtonElement,
  HTMLTextAreaElement, HTMLCanvasElement,
  HTMLAudioElement, HTMLLabelElement,
  HTMLDialogElement, HTMLSelectElement,
  HTMLInputElement, HTMLDetailsElement,
  HTMLAnchorElement
];

/**
 * Check is element checked or selected.
 *
 * @param {HTMLElement} element element to be
 * @returns {boolean} true if elemented has selected or checked attribute true.
 */
const isSelectedOrChecked = element => element.value && (element.selected || element.checked);

/**
 * Parse selected values from select
 *
 * @param {HTMLElement[]} data html elements from form object
 * @param {Function} filterFunction function used to filter selected/checked options
 * @returns {String} comma separated string of selected values or empty string if no values
 */
const stringifyFilterArray = (data, filterFunction ) => {
  if( data instanceof HTMLElement || data instanceof RadioNodeList ) {
    return Array.from(data)?.filter( filterFunction )
      ?.map( element => element.value ).join(', ')
  } else {
    return '';
  }
}

/*
* concatenate min and max values if both exists
* @param {string} min value
* @param {string} max value
* @returns {string} concatenated string of min and max values
*/
const stringifyMinMaxValue = (min, max) => {
  if(min && max) {
    return min + ' - ' + max;
  } else {
    return min || max || '';
  }
}

/**
 * Send car search event to data layer
 *
 * @param {FormElement} form element
 * @param {object} params additional params
 * @returns {object} event object
 *
 */
export const formCarSearchEvent = (form, params) => {
  window.vehicleSearchFormSubmitted = false;

  const event = {
    name: 'search_action' ,
    params: {
      'vehicle_category': stringifyFilterArray(form['sales_type[]'], isSelectedOrChecked),
      'driveTrain': stringifyFilterArray(form['drive[]'], isSelectedOrChecked),
      'fuelType': stringifyFilterArray(form['fuel[]'], isSelectedOrChecked),
      'gearType': stringifyFilterArray(form['transmission[]'], isSelectedOrChecked),
      'bodyType': stringifyFilterArray(form['body_type[]'], isSelectedOrChecked),
      'manufacturer': form?.make?.value ?? '',
      'manufacturerYear_min': form?.min_model_year?.value ?? '',
      'manufacturerYear_max': form?.max_model_year?.value ?? '',
      'mileage_min': form?.min_mileage?.value ?? '',
      'mileage_max': form?.max_mileage?.value ?? '',
      'model': stringifyFilterArray(form['model[]'], isSelectedOrChecked),
      'price_min':  form?.min_price?.value ?? '',
      'price_max':  form?.max_price?.value ?? '',
      'monthlyPrice_max':  form?.max_monthly_installment?.value ?? '',
      'color': form?.color?.value ?? '',
      'id': form?.car_id?.value ?? '', //this is just added for future use
      'outlet': form?.outlet?.value ?? 'Espoo', //just one outlet for now
      'registerNumber': form?.license_part?.value ?? '',
      'consumption_max': form?.max_consumption?.value ?? '',
      ...params,
    }
  }

  return event;
}


/* TODO: check these regexps after domain change */
export const isHelkama = new RegExp(/^(https|http)(:\/\/helkama-autokauppa\.fi)/);
export const isHelkamaAdmin = new RegExp(/^(https|http)(:\/\/skoda-espoo-admin\.stage\.crasman\.fi)/);
export const isHelkamaLocal = new RegExp(/^(https|http)(:\/\/vcap\.me:9000)/);
/**
 * Is url internal URL -address
 *
 * @param {string?} url url address
 * @returns {boolean} true if url is internal url address false if not
 */
export const isInternalUrl = ( url ) => {
  let isInternalUrl = false;

  if( typeof url === 'string' ) {
    isInternalUrl = isHelkama.test(url) || isHelkamaAdmin.test(url) || isHelkamaLocal.test(url);
  }

  return isInternalUrl;
}

/**
 * Has event car context
 *
 * @param {string} eventName event name
 * @returns {boolean} true if event has a car context
 */
export const hasEventCarContext = (eventName) => {
  return EVENTS[eventName]?.carContext || false;
}

/**
 * Try to get car context object from window object.
 *
 * @returns {object} car context object
 */
export const getCarContextData = () => {
  return window?.carContext || {};
}


/**
 * Try to get car context object from link. When window.carContext object is not available.
 * Dataset should contain car context data.
 *
 * @param {string} dataset link to car containing car context data
 * @returns {object} car context object
 */

export const getCarCardContextData = (eventName, dataset) => {

  return {
    'car_date_added': dataset?.carDateAdded ?? '',
    'car_outlet':  dataset?.location ?? 'Espoo',
    'car_manufacturer':  dataset?.manufacturer ?? '',
    'car_model':  dataset?.model ?? '',
    'car_register_number':  dataset?.license ?? '',
    'car_manufacturer_year':  dataset?.modelYear ?? '',
    'car_price':  dataset?.price ? `${dataset?.price} €` : '',
    'car_monthly_price':  dataset?.monthlyPrice ?? '',
    'car_mileage': dataset?.mileage ? `${dataset?.mileage}` : '',
    'car_color':  dataset?.color ?? '',
    'car_gear_type':  dataset?.transmission ?? '',
    'car_fuel_type':  dataset?.fuel ?? '',
    'car_drive_type':  dataset?.driveType ?? '',
    'car_id':  dataset?.externalId ?? '',
    'car_vehicle_type':  dataset?.bodyType ?? '',
  };
}

/**
 * Try to get pageType object from window object.
 *
 * @returns {object} page_type object from pageData or fallback to page_type content
 */
export const getPageData = () => {
  return window?.pageData || {page_type: 'content'};
}

/**
 * Is passed object interactive HTMLElement
 *
 * @param {*} element element to be checked for interactivity :P
 * @returns {boolean} true if element is considered to be interactive element false otherwise
 */
export const isInterActiveElement = (element) => {
  return element && interactiveElements.some( type => element instanceof type );
}


/**
 * Return interactive element's related text
 *
 * @param {HTMLElement?} element mouse click target element
 * @return {string} text
 */
export const getInterActiveElementText = (element) => {

  let text = '';

  if( element instanceof HTMLElement ) {

    if( element.id ) {

      const elementLabel = document.querySelector(`[for="${element.id}"]`);

      if( elementLabel ) {

        text = elementLabel.innerText;

      }

    } else if( element instanceof HTMLInputElement && element.type === 'submit' ) {

      text = element.value;

    } else {

      if (element.ariaLabel) {

        text = element.ariaLabel;

      } else {

        text = element.innerText;

      }
    }
  }

  return text;
}

/**
 * Find closest element with data-gtm-event-page-section attribute and return value of it.
 *
 * @param {HTMLElement} element clicked element
 *
 * @returns {String} data-gtm-event-page-section value
 */
export const getClosestPageSection = (element) => {
  return element.closest('[data-gtm-event-page-section]')?.dataset?.gtmEventPageSection ?? '';
}

/**
 * Loop through classlist and check if class name contains partial match
 *
 * @param {HTMLElement} element clicked element
 * @param {string} partialClass partial class name
 *
 * @returns {boolean}
 */
export const hasPartialClassMatch = (element, partialClass) => {
  return element.classList.value.split(' ').some( className => className.includes(partialClass) );
}

/**
 * If clicked element is a child of the sites main navigation return true
 * @param {HTMLElement} element clicked element
 * @returns {boolean}
 */
export const isMainNavigationChild = (element) => { return element.closest('.site-navigation') !== null; }


/**
 * get additional information about the form
 *
 * @param {event} event event object
 * @returns {object} object
 */
export const additionalFormData = (event) => {

  const elem = (event.target.nodeName === 'FORM') ? event.target : event.target.closest('[data-gtm-event-form-type]');

  return {
    'form_type': elem?.dataset.gtmEventFormType,
    'page_section': getClosestPageSection(elem),
    'contact_type': elem?.dataset.gtmEventContactType + ' - ' + elem.dataset.gtmEventFormType,
  }
}
